import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import type { PerspectiveCamera as ThreePerspectiveCamera } from "three";
import { useStore } from "../store.js";
import { ZOOM_MAX, ZOOM_MIN, zoomToCamera } from "./zoom.js";

/**
 * Wheel input maps a single notch (~100 px deltaY) to ~0.15 zoom units,
 * tuned to feel like one decisive layer step per ~3 notches.
 */
const WHEEL_STEP = 0.0015;
/** Pixels of pinch-distance change per zoom unit; positive pinch-out = closer. */
const PINCH_STEP = 0.005;
const SMOOTHING = 0.18;

function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

export function ContinuousZoomCamera(): null {
  const { camera, gl } = useThree();
  const cam = camera as ThreePerspectiveCamera;
  const setZoom = useStore((s) => s.setZoom);
  const currentZoom = useRef(useStore.getState().zoom);
  const pinchPrevDist = useRef(0);

  useEffect(() => {
    const dom = gl.domElement;

    const onWheel = (ev: WheelEvent): void => {
      ev.preventDefault();
      const next = clamp(useStore.getState().zoom + ev.deltaY * WHEEL_STEP, ZOOM_MIN, ZOOM_MAX);
      setZoom(next);
    };

    const onTouchStart = (ev: TouchEvent): void => {
      if (ev.touches.length === 2) {
        const dx = ev.touches[0]!.clientX - ev.touches[1]!.clientX;
        const dy = ev.touches[0]!.clientY - ev.touches[1]!.clientY;
        pinchPrevDist.current = Math.hypot(dx, dy);
      }
    };

    const onTouchMove = (ev: TouchEvent): void => {
      if (ev.touches.length !== 2) return;
      ev.preventDefault();
      const dx = ev.touches[0]!.clientX - ev.touches[1]!.clientX;
      const dy = ev.touches[0]!.clientY - ev.touches[1]!.clientY;
      const dist = Math.hypot(dx, dy);
      if (pinchPrevDist.current > 0) {
        const delta = (pinchPrevDist.current - dist) * PINCH_STEP;
        const next = clamp(useStore.getState().zoom + delta, ZOOM_MIN, ZOOM_MAX);
        setZoom(next);
      }
      pinchPrevDist.current = dist;
    };

    const onTouchEnd = (): void => {
      pinchPrevDist.current = 0;
    };

    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("touchstart", onTouchStart, { passive: true });
    dom.addEventListener("touchmove", onTouchMove, { passive: false });
    dom.addEventListener("touchend", onTouchEnd);
    dom.addEventListener("touchcancel", onTouchEnd);

    return () => {
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("touchstart", onTouchStart);
      dom.removeEventListener("touchmove", onTouchMove);
      dom.removeEventListener("touchend", onTouchEnd);
      dom.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [gl, setZoom]);

  useFrame(() => {
    // Read the latest target each frame to avoid stale-closure traps and to
    // remain correct if zoom is mutated outside the React tree (devtools, ws).
    const target = useStore.getState().zoom;
    currentZoom.current += (target - currentZoom.current) * SMOOTHING;
    const resolved = zoomToCamera(currentZoom.current);
    cam.position.set(resolved.position[0], resolved.position[1], resolved.position[2]);
    cam.fov = resolved.fov;
    cam.lookAt(0, 0, 0);
    cam.updateProjectionMatrix();
  });

  return null;
}
